import React, { useState } from "react";
import { ChevronRight, ChevronDown, Users2, ShieldCheck, Wallet, FileText } from "lucide-react";
import { inr } from "@/lib/api";

const LEVEL_COLORS = [
  "border-teal-300 bg-teal-50",
  "border-sky-300 bg-sky-50",
  "border-violet-300 bg-violet-50",
  "border-amber-300 bg-amber-50",
  "border-rose-300 bg-rose-50",
  "border-slate-300 bg-slate-50",
];

export function ReferralTree({ data, emptyText = "Abhi tak koi network nahi hai." }) {
  const [collapsed, setCollapsed] = useState({});
  const toggle = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  const rows = [];
  const walk = (nodes, depth, ancestors) => {
    for (const n of nodes) {
      const hidden = ancestors.some((a) => collapsed[a]);
      rows.push({ node: n, depth, hidden, hasKids: !!(n.children && n.children.length) });
      if (n.children && n.children.length) walk(n.children, depth + 1, [...ancestors, n.id]);
    }
  };
  walk((data && data.tree) || [], 0, []);
  const visible = rows.filter((r) => !r.hidden);

  return (
    <div className="space-y-5" data-testid="referral-tree">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <p className="text-[11px] uppercase font-semibold text-slate-400">Direct (L1)</p>
          <p className="font-mono-num font-extrabold text-2xl text-[#0B223D]" data-testid="tree-direct">{data?.total_direct || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <p className="text-[11px] uppercase font-semibold text-slate-400">Total Members</p>
          <p className="font-mono-num font-extrabold text-2xl text-[#0B223D]" data-testid="tree-total">{data?.total_members || 0}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <p className="text-[11px] uppercase font-semibold text-slate-400">Levels</p>
          <p className="font-mono-num font-extrabold text-2xl text-[#0B223D]" data-testid="tree-levels">{(data?.levels || []).length}</p>
        </div>
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4">
          <p className="text-[11px] uppercase font-semibold text-teal-600 flex items-center gap-1"><Wallet className="w-3 h-3" /> Network Earnings</p>
          <p className="font-mono-num font-extrabold text-2xl text-teal-700" data-testid="tree-network-earnings">{inr(data?.network_earnings || 0)}</p>
        </div>
      </div>

      <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4 inline-flex items-center gap-2" data-testid="tree-network-leads-card">
        <FileText className="w-4 h-4 text-sky-600" />
        <span className="text-sm font-semibold text-sky-800">Network Leads: <span className="font-mono-num" data-testid="tree-network-leads">{data?.network_leads || 0}</span></span>
      </div>

      {(data?.levels || []).length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="tree-level-chips">
          {data.levels.map((l) => (
            <span key={l.level} className="text-xs font-semibold bg-slate-100 text-slate-600 rounded-full px-3 py-1">
              Level {l.level}: <span className="font-mono-num text-[#0B223D]">{l.count}</span>
            </span>
          ))}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500">{emptyText}</div>
      ) : (
        <div className="space-y-2">
          {visible.map(({ node, depth, hasKids }) => {
            const color = LEVEL_COLORS[(node.level - 1) % LEVEL_COLORS.length];
            const isOpen = !collapsed[node.id];
            return (
              <div key={node.id} style={{ marginLeft: depth * 20 }} className="relative">
                {depth > 0 && <span className="absolute -left-3 top-1/2 w-3 border-t-2 border-slate-200" />}
                <div className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 ${color}`} data-testid={`tree-node-${node.user_code}`}>
                  <button
                    type="button"
                    onClick={() => hasKids && toggle(node.id)}
                    className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-md ${hasKids ? "hover:bg-white/60 text-slate-600" : "text-transparent cursor-default"}`}
                    data-testid={`tree-toggle-${node.user_code}`}
                    aria-label={isOpen ? "Collapse" : "Expand"}
                  >
                    {hasKids ? (isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : <span className="w-4 h-4 inline-block" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#0B223D] truncate">{node.name}</span>
                      {node.kyc_status === "verified" && <ShieldCheck className="w-3.5 h-3.5 text-teal-600 shrink-0" />}
                    </div>
                    <p className="text-[11px] font-mono-num text-slate-500">{node.user_code} · L{node.level}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Leads</p>
                    <p className="font-mono-num font-bold text-sm text-sky-700" data-testid={`tree-leads-${node.user_code}`}>{node.leads || 0}{node.conversions ? <span className="text-teal-600"> · {node.conversions}✓</span> : null}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Earned</p>
                    <p className="font-mono-num font-bold text-sm text-teal-700">{inr(node.earnings || 0)}</p>
                  </div>
                  {hasKids && (
                    <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 bg-white/70 rounded-full px-2 py-0.5">
                      <Users2 className="w-3 h-3" /> {node.children.length}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
